import type { FieldValues, Path, UseFormSetError } from 'react-hook-form';

import type { Result } from 'utils/result';

export interface FormError {
	type?: string;
	message: string;
}

/** Errors to set on a form after a submit. */
export interface FormErrors {
	/** Set on the field, or as a general error when the field isn't among the submitted values. */
	fields?: Record<string, FormError>;
	/** Set as `root.<key>`. */
	general?: Record<string, FormError>;
}

/**
 * Get form errors from an API result.
 *
 * Returns `undefined` on success. Rethrows failures other than
 * `ValidationError`, for `Form` to show as a critical error.
 */
export function getValidationFormErrors(
	result: Result<unknown, Error>,
): FormErrors | undefined {
	if (result.isSuccess()) {
		return undefined;
	}
	if (!result.isFailure('ValidationError')) {
		throw result.error;
	}
	const fields: Record<string, FormError> = {};
	Object.entries(result.error.fieldErrors).forEach(([fieldName, errors]) => {
		const firstError = errors[0];
		if (firstError?.text) {
			fields[fieldName] = { message: firstError.text, type: firstError.key };
		}
	});
	const general: Record<string, FormError> = {};
	result.error.generalErrors.forEach((error) => {
		general[error.key] = { message: error.text };
	});
	return { fields, general };
}

/**
 * Set form errors on a form.
 *
 * A field error is set as a root error when its field isn't part of the
 * submitted values, since an error on a field that isn't rendered can't be
 * seen or cleared and blocks the form.
 */
export function setFormErrors<T extends FieldValues>({
	errors,
	setError,
	values,
}: {
	errors: FormErrors;
	setError: UseFormSetError<T>;
	values: T;
}) {
	const fieldNames = Object.keys(values);
	Object.entries(errors.fields ?? {}).forEach(([fieldName, error]) => {
		if (fieldNames.includes(fieldName)) {
			// Checked against the submitted values above.
			setError(fieldName as Path<T>, error);
		} else {
			setError(`root.${fieldName}`, error);
		}
	});
	Object.entries(errors.general ?? {}).forEach(([key, error]) => {
		setError(`root.${key}`, error);
	});
}
